import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Building2,
  Cloud,
  CloudOff,
  LogOut,
  Menu,
  Plus,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthPage } from "./components/AuthPage";
import {
  CloudWorkspaceGate,
  type CloudControls,
} from "./components/CloudWorkspaceGate";
import { Shell, type Section } from "./components/Shell";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { DutiesView } from "./views/DutiesView";
import { ApprovalsView } from "./views/ApprovalsView";
import { CompetenceView } from "./views/CompetenceView";
import { ControlsView } from "./views/ControlsView";
import { DocumentsView } from "./views/DocumentsView";
import { DeliverablesView } from "./views/DeliverablesView";
import { EvidenceView } from "./views/EvidenceView";
import { HirarcView } from "./views/HirarcView";
import { IncidentsView } from "./views/IncidentsView";
import { OverviewView } from "./views/OverviewView";
import { ProjectView } from "./views/ProjectView";
import { ContractorsView } from "./views/ContractorsView";
import { ReportsView } from "./views/ReportsView";
import { RegulatoryChangesView } from "./views/RegulatoryChangesView";
import { RisksView } from "./views/RisksView";
import { FieldVerificationView } from "./views/FieldVerificationView";
import { workspaceMetrics } from "./lib/assessmentEngine";
import {
  blankWorkspace,
  clearWorkspace,
  demoWorkspace,
  loadWorkspace,
  saveWorkspace,
} from "./lib/projectStore";
import { persistApprovalDecision } from "./lib/cloudWorkspace";
import { isSupabaseConfigured } from "./lib/supabase";
import { createProjectFileUrl, uploadProjectFile } from "./lib/fileStorage";
import {
  compareRevisions,
  inspectRevisionFile,
} from "./lib/revisionIntelligence";
import type {
  AuditEntry,
  CompetenceRecord,
  CriticalControl,
  DesignRisk,
  DocumentRevision,
  DutyAction,
  EvidenceItem,
  HirarcAssessment,
  IncidentRecord,
  Project,
  ProjectWorkspace,
  TrainingAssignment,
} from "./types/domain";

type AuditedCollectionKey =
  | "risks"
  | "evidence"
  | "dutyActions"
  | "hirarcAssessments"
  | "incidents"
  | "criticalControls"
  | "approvals"
  | "documentRevisions"
  | "revisionComparisons"
  | "safetyIssuePins"
  | "deliverables"
  | "regulatoryChanges"
  | "fieldVerifications"
  | "competenceRecords"
  | "trainingAssignments";

const auditedCollections: { key: AuditedCollectionKey; label: string }[] = [
  { key: "risks", label: "design risk" },
  { key: "evidence", label: "evidence" },
  { key: "dutyActions", label: "duty action" },
  { key: "hirarcAssessments", label: "HIRARC" },
  { key: "incidents", label: "incident" },
  { key: "criticalControls", label: "critical control" },
  { key: "approvals", label: "approval" },
  { key: "documentRevisions", label: "document revision" },
  { key: "revisionComparisons", label: "revision comparison" },
  { key: "safetyIssuePins", label: "safety issue" },
  { key: "deliverables", label: "controlled deliverable" },
  { key: "regulatoryChanges", label: "regulatory change" },
  { key: "fieldVerifications", label: "field verification" },
  { key: "competenceRecords", label: "competence record" },
  { key: "trainingAssignments", label: "training assignment" },
];

const auditWorkspaceChange = (
  current: ProjectWorkspace,
  next: ProjectWorkspace,
  actor: string,
): AuditEntry => {
  if (JSON.stringify(current.project) !== JSON.stringify(next.project)) {
    return {
      id: crypto.randomUUID(),
      occurredAt: new Date().toISOString(),
      actor,
      action: "project.updated",
      entityType: "project",
      entityId: next.project.id,
      summary: `Updated project facts for ${next.project.name || "untitled project"}.`,
    };
  }

  for (const { key, label } of auditedCollections) {
    const before = current[key] as { id: string }[];
    const after = next[key] as { id: string }[];
    if (before === after || JSON.stringify(before) === JSON.stringify(after)) continue;
    const added = after.find((item) => !before.some((existing) => existing.id === item.id));
    const removed = before.find((item) => !after.some((existing) => existing.id === item.id));
    const changed = after.find((item) => {
      const previous = before.find((existing) => existing.id === item.id);
      return previous && JSON.stringify(previous) !== JSON.stringify(item);
    });
    const operation = added ? "created" : removed ? "removed" : "updated";
    const entity = added ?? removed ?? changed;
    return {
      id: crypto.randomUUID(),
      occurredAt: new Date().toISOString(),
      actor,
      action: `${key}.${operation}`,
      entityType: label,
      entityId: entity?.id ?? next.project.id,
      summary: `${operation[0].toUpperCase()}${operation.slice(1)} ${label} record.`,
    };
  }

  return {
    id: crypto.randomUUID(),
    occurredAt: new Date().toISOString(),
    actor,
    action: "workspace.updated",
    entityType: "workspace",
    entityId: next.project.id,
    summary: "Updated the project assurance workspace.",
  };
};

const sectionTitles: Record<Section, { eyebrow: string; title: string; description: string }> = {
  overview: {
    eyebrow: "Project assurance",
    title: "Design decisions you can defend.",
    description:
      "See what is known, what remains unsupported and what needs a named reviewer before the project moves forward.",
  },
  project: {
    eyebrow: "Controlled intake",
    title: "Confirm the project facts.",
    description:
      "These facts drive the CDM duty map. Unknown values remain visible instead of being silently assumed.",
  },
  duties: {
    eyebrow: "Malaysia regulatory intelligence",
    title: "Turn every applicable provision into accountable work.",
    description:
      "Search all 39 CDM regulations, open the exact Gazette page, assign ownership and close each duty with evidence.",
  },
  risks: {
    eyebrow: "Prevention through design",
    title: "Record why the design is safe.",
    description:
      "Capture elimination decisions, residual risks and the drawing or specification that carries the control.",
  },
  hirarc: {
    eyebrow: "DOSH risk assessment",
    title: "Move from hazard to verified residual risk.",
    description:
      "Use the official 5×5 HIRARC method, hierarchy of controls, named PIC and scheduled review triggers.",
  },
  controls: {
    eyebrow: "Fatal-risk assurance",
    title: "Know whether critical controls will work.",
    description:
      "Define performance standards and verification evidence for preventive and mitigative barriers.",
  },
  incidents: {
    eyebrow: "Learning system",
    title: "Make incident learning change the design.",
    description:
      "Connect causes, failed controls and transferable lessons to live project risks and decisions.",
  },
  documents: {
    eyebrow: "Safety information golden thread",
    title: "Control revisions and expose change impact.",
    description:
      "Link each safety decision to the exact drawing, specification, model or calculation revision that carries it.",
  },
  deliverables: {
    eyebrow: "Controlled CDM outputs",
    title: "Compile, review and issue the required project documents.",
    description:
      "Build PCI, DRA, appointments, notification records, construction plans and the Safety and Health File directly from the assurance graph.",
  },
  changes: {
    eyebrow: "Regulatory change control",
    title: "Know which live project decisions a rule change affects.",
    description:
      "Map each authoritative update to applicable duties, design risks, named owners and controlled impact decisions.",
  },
  field: {
    eyebrow: "Mobile verification",
    title: "Verify critical controls where the work happens.",
    description:
      "Issue secure QR challenges for installation, inspection, pre-start and handover checks and retain the result in the project trail.",
  },
  competence: {
    eyebrow: "Regulation 10 capability",
    title: "Assure competence and close knowledge gaps.",
    description:
      "Record evidence of capability and assign contextual training for each project role and hazard.",
  },
  approvals: {
    eyebrow: "Accountable review",
    title: "Keep authorship, review and acceptance distinct.",
    description:
      "Route safety-critical records to named reviewers and retain each decision, comment and requested change.",
  },
  evidence: {
    eyebrow: "Safety Assurance Graph",
    title: "Close the evidence chain.",
    description:
      "Control appointments, drawings, plans and records without losing ownership, status or revision history.",
  },
  reports: {
    eyebrow: "Controlled outputs",
    title: "Prepare review and handover packs.",
    description:
      "Export a transparent record of project facts, duties, risks, evidence and unresolved limitations.",
  },
  contractors: {
    eyebrow: "Supply Chain Gateway",
    title: "Manage contractor compliance and liability.",
    description: "Monitor organizational readiness and block unauthorized work from non-compliant tiers.",
  },
};

const sectionPaths: Record<Section, string> = {
  overview: "/",
  project: "/project",
  duties: "/duties",
  risks: "/risks",
  hirarc: "/hirarc",
  controls: "/controls",
  incidents: "/incidents",
  documents: "/documents",
  deliverables: "/deliverables",
  changes: "/changes",
  field: "/field",
  competence: "/competence",
  approvals: "/approvals",
  evidence: "/evidence",
  reports: "/reports",
  contractors: "/contractors",
};

const pathSections = Object.fromEntries(
  Object.entries(sectionPaths).map(([section, path]) => [path, section]),
) as Record<string, Section>;

const upsertById = <T extends { id: string }>(items: T[], item: T): T[] =>
  items.some((existing) => existing.id === item.id)
    ? items.map((existing) => (existing.id === item.id ? item : existing))
    : [...items, item];

export default function App() {
  if (!isSupabaseConfigured) {
    return <LocalProduct />;
  }

  return (
    <AuthProvider>
      <CloudProduct />
    </AuthProvider>
  );
}

function CloudProduct() {
  const { loading, session } = useAuth();

  if (loading) {
    return (
      <main className="cloud-loading" role="status">
        <span className="brand-mark">DS</span>
        <strong>Opening Design Safety MY…</strong>
      </main>
    );
  }
  if (!session) return <AuthPage />;

  return (
    <CloudWorkspaceGate>
      {(workspace, setWorkspace, controls) => (
        <WorkspaceApplication
          workspace={workspace}
          onWorkspaceChange={setWorkspace}
          persistenceMode="cloud"
          cloud={controls}
        />
      )}
    </CloudWorkspaceGate>
  );
}

function LocalProduct() {
  const [workspace, setWorkspace] = useState<ProjectWorkspace>(
    () => loadWorkspace() ?? demoWorkspace(),
  );

  useEffect(() => {
    saveWorkspace(workspace);
  }, [workspace]);

  return (
    <WorkspaceApplication
      workspace={workspace}
      onWorkspaceChange={setWorkspace}
      persistenceMode="browser"
    />
  );
}

function WorkspaceApplication({
  workspace,
  onWorkspaceChange: persistWorkspaceChange,
  persistenceMode,
  cloud,
}: {
  workspace: ProjectWorkspace;
  onWorkspaceChange: (workspace: ProjectWorkspace) => void;
  persistenceMode: "browser" | "cloud";
  cloud?: CloudControls;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const section = pathSections[location.pathname] ?? "overview";

  useEffect(() => {
    if (!pathSections[location.pathname]) {
      navigate("/", { replace: true });
    }
  }, [location.pathname, navigate]);

  const metrics = useMemo(() => workspaceMetrics(workspace), [workspace]);
  const heading = sectionTitles[section];
  const setSection = (next: Section) => {
    navigate(sectionPaths[next]);
  };
  const onWorkspaceChange = (next: ProjectWorkspace) => {
    const auditEntry = auditWorkspaceChange(
      workspace,
      next,
      cloud?.userEmail ?? "Local workspace user",
    );
    persistWorkspaceChange({
      ...next,
      auditTrail: [...(next.auditTrail ?? []), auditEntry].slice(-500),
    });
  };

  const updateProject = (project: Project) => {
    onWorkspaceChange({
      ...workspace,
      project: { ...project, updatedAt: new Date().toISOString() },
    });
  };

  const saveRisk = (risk: DesignRisk) => {
    const exists = workspace.risks.some((item) => item.id === risk.id);
    onWorkspaceChange({
      ...workspace,
      risks: exists
        ? workspace.risks.map((item) => (item.id === risk.id ? risk : item))
        : [...workspace.risks, risk],
    });
  };

  const removeRisk = (riskId: string) => {
    onWorkspaceChange({
      ...workspace,
      risks: workspace.risks.filter((item) => item.id !== riskId),
      hirarcAssessments: workspace.hirarcAssessments.map((item) => ({
        ...item,
        linkedRiskIds: item.linkedRiskIds.filter((id) => id !== riskId),
      })),
      criticalControls: workspace.criticalControls.map((item) => ({
        ...item,
        linkedRiskIds: item.linkedRiskIds.filter((id) => id !== riskId),
      })),
      incidents: workspace.incidents.map((item) => ({
        ...item,
        linkedRiskIds: item.linkedRiskIds.filter((id) => id !== riskId),
      })),
      documentRevisions: workspace.documentRevisions.map((item) => ({
        ...item,
        linkedRiskIds: item.linkedRiskIds.filter((id) => id !== riskId),
      })),
    });
  };

  const saveEvidence = (evidence: EvidenceItem) => {
    const exists = workspace.evidence.some((item) => item.id === evidence.id);
    onWorkspaceChange({
      ...workspace,
      evidence: exists
        ? workspace.evidence.map((item) => (item.id === evidence.id ? evidence : item))
        : [...workspace.evidence, evidence],
    });
  };

  const removeEvidence = (evidenceId: string) => {
    onWorkspaceChange({
      ...workspace,
      evidence: workspace.evidence.filter((item) => item.id !== evidenceId),
      risks: workspace.risks.map((risk) => ({
        ...risk,
        evidenceIds: risk.evidenceIds.filter((id) => id !== evidenceId),
      })),
      dutyActions: workspace.dutyActions.map((item) => ({
        ...item,
        evidenceIds: item.evidenceIds.filter((id) => id !== evidenceId),
      })),
      hirarcAssessments: workspace.hirarcAssessments.map((item) => ({
        ...item,
        evidenceIds: item.evidenceIds.filter((id) => id !== evidenceId),
      })),
      criticalControls: workspace.criticalControls.map((item) => ({
        ...item,
        evidenceIds: item.evidenceIds.filter((id) => id !== evidenceId),
      })),
    });
  };

  const saveDutyAction = (action: DutyAction) =>
    onWorkspaceChange({ ...workspace, dutyActions: upsertById(workspace.dutyActions, action) });

  const saveHirarc = (assessment: HirarcAssessment) =>
    onWorkspaceChange({
      ...workspace,
      hirarcAssessments: upsertById(workspace.hirarcAssessments, assessment),
    });

  const saveControl = (control: CriticalControl) =>
    onWorkspaceChange({
      ...workspace,
      criticalControls: upsertById(workspace.criticalControls, control),
    });

  const saveIncident = (incident: IncidentRecord) =>
    onWorkspaceChange({
      ...workspace,
      incidents: upsertById(workspace.incidents, incident),
    });

  const saveRevision = (revision: DocumentRevision) => {
    const previous = revision.supersedesId
      ? workspace.documentRevisions.find((item) => item.id === revision.supersedesId)
      : undefined;
    if (
      previous &&
      previous.extractedPages.length > 0 &&
      revision.extractedPages.length > 0
    ) {
      const comparison = compareRevisions(previous, revision);
      const comparisonId =
        workspace.revisionComparisons.find(
          (item) =>
            item.previousRevisionId === previous.id &&
            item.currentRevisionId === revision.id,
        )?.id ?? comparison.id;
      const finalComparison = { ...comparison, id: comparisonId };
      const reopened = new Set(finalComparison.reopenedRiskIds);
      onWorkspaceChange({
        ...workspace,
        documentRevisions: upsertById(workspace.documentRevisions, {
          ...revision,
          comparisonId,
        }),
        revisionComparisons: upsertById(
          workspace.revisionComparisons,
          finalComparison,
        ),
        risks: workspace.risks.map((risk) =>
          reopened.has(risk.id)
            ? {
                ...risk,
                previousReviewStatus: risk.reviewStatus,
                reviewStatus: "draft",
                reopenedAt: new Date().toISOString(),
                reopenReason: `Safety-relevant change detected between ${previous.documentNumber} ${previous.revision} and ${revision.revision}: ${finalComparison.safetyKeywords.join(", ") || `${finalComparison.changeScore}% textual change`}.`,
              }
            : risk,
        ),
      });
      return;
    }
    onWorkspaceChange({
      ...workspace,
      documentRevisions: upsertById(workspace.documentRevisions, revision),
    });
  };

  const saveCompetence = (record: CompetenceRecord) =>
    onWorkspaceChange({
      ...workspace,
      competenceRecords: upsertById(workspace.competenceRecords, record),
    });

  const saveTraining = (assignment: TrainingAssignment) =>
    onWorkspaceChange({
      ...workspace,
      trainingAssignments: upsertById(workspace.trainingAssignments, assignment),
    });

  const saveDeliverable = (deliverable: ProjectWorkspace["deliverables"][number]) =>
    onWorkspaceChange({
      ...workspace,
      deliverables: upsertById(workspace.deliverables, deliverable),
    });

  const saveRegulatoryChange = (
    change: ProjectWorkspace["regulatoryChanges"][number],
  ) => {
    const reopenIds = new Set(
      change.impactStatus === "action-required" ? change.impactedRiskIds : [],
    );
    onWorkspaceChange({
      ...workspace,
      regulatoryChanges: upsertById(workspace.regulatoryChanges, change),
      dutyActions: workspace.dutyActions.map((action) =>
        change.impactedDutyIds.includes(action.ruleId)
          ? { ...action, status: "open", updatedAt: new Date().toISOString() }
          : action,
      ),
      risks: workspace.risks.map((risk) =>
        reopenIds.has(risk.id)
          ? {
              ...risk,
              previousReviewStatus: risk.reviewStatus,
              reviewStatus: "draft",
              reopenedAt: new Date().toISOString(),
              reopenReason: `Regulatory change impact: ${change.instrument}`,
            }
          : risk,
      ),
    });
  };

  const saveFieldVerification = (
    verification: ProjectWorkspace["fieldVerifications"][number],
  ) =>
    onWorkspaceChange({
      ...workspace,
      fieldVerifications: upsertById(
        workspace.fieldVerifications,
        verification,
      ),
    });

  const saveApproval = (approval: ProjectWorkspace["approvals"][number]) =>
  {
    const previous = workspace.approvals.find((item) => item.id === approval.id);
    onWorkspaceChange({
      ...workspace,
      approvals: upsertById(workspace.approvals, approval),
    });
    if (cloud && approval.status !== "requested" && previous?.status === "requested") {
      void persistApprovalDecision(
        cloud.activeProjectId,
        cloud.organization.id,
        cloud.userId,
        approval,
      ).catch((error) => {
        console.error("Unable to persist immutable approval decision", error);
      });
    }
  };

  const uploadRevisionFile = async (revision: DocumentRevision, file: File) => {
    if (!cloud) throw new Error("Sign in to upload controlled project files.");
    const allowedExtension =
      /\.(pdf|png|jpe?g|webp|docx?|xlsx?|pptx?|ifc|bcf|txt|csv)$/i.test(file.name);
    if (!allowedExtension)
      throw new Error(
        "Unsupported file type. Use PDF, image, Office, IFC, BCF, TXT or CSV files.",
      );
    if (file.size < 1) throw new Error("The selected file is empty.");
    if (file.size > 50 * 1024 * 1024) throw new Error("The maximum file size is 50 MB.");
    const intelligence = await inspectRevisionFile(file);
    const uploaded = await uploadProjectFile(
      cloud.activeProjectId,
      cloud.organization.id,
      cloud.userId,
      revision.id,
      file,
    );
    return {
      ...revision,
      fileName: file.name,
      filePath: uploaded.path,
      fileSize: file.size,
      mimeType: file.type || "application/octet-stream",
      checksum: uploaded.checksum,
      ...intelligence,
      updatedAt: new Date().toISOString(),
    };
  };

  const openRevisionFile = async (path: string) => {
    const url = await createProjectFileUrl(path);
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const loadDemo = () => {
    onWorkspaceChange(demoWorkspace());
    setSection("overview");
  };

  const startFresh = () => {
    if (persistenceMode === "browser") clearWorkspace();
    onWorkspaceChange(blankWorkspace());
    setSection("project");
  };

  return (
    <div className="app-shell">
      <Shell
        section={section}
        onSectionChange={(next) => {
          setSection(next);
          setSidebarOpen(false);
        }}
        open={sidebarOpen}
        metrics={metrics}
      />

      <main className="main-panel">
        <div className="assurance-banner">
          <div>
            <AlertTriangle size={16} />
            <span>
              {persistenceMode === "cloud"
                ? "Controlled workspace — project records and private files are organisation-scoped with an accountable evidence trail."
                : "Local workspace — records remain in this browser. Sign in to use organisation access control and private files."}
            </span>
          </div>
          <span className="policy-version">
            Policy pack 1.0 · CDM + Malaysian regulatory overlays
          </span>
        </div>

        {cloud && <CloudToolbar controls={cloud} />}

        <header className="page-header">
          <button
            className="mobile-menu"
            type="button"
            aria-label="Open navigation"
            onClick={() => setSidebarOpen((open) => !open)}
          >
            <Menu size={22} />
          </button>
          <div className="page-heading">
            <span className="eyebrow">{heading.eyebrow}</span>
            <h1>{heading.title}</h1>
            <p>{heading.description}</p>
          </div>
          <div className="project-chip">
            <span className="project-chip-icon">
              <ShieldCheck size={18} />
            </span>
            <span>
              <small>Active project</small>
              <strong>{workspace.project.name || "Not yet named"}</strong>
            </span>
          </div>
        </header>

        <div className="content-wrap">
          {section === "overview" && (
            <OverviewView
              workspace={workspace}
              metrics={metrics}
              onNavigate={setSection}
              onLoadDemo={loadDemo}
              onStartFresh={startFresh}
            />
          )}
          {section === "project" && (
            <ProjectView project={workspace.project} onSave={updateProject} />
          )}
          {section === "contractors" && (
            <ContractorsView workspace={workspace} />
          )}
          {section === "duties" && (
            <DutiesView
              project={workspace.project}
              actions={workspace.dutyActions}
              evidence={workspace.evidence}
              onSaveAction={saveDutyAction}
            />
          )}
          {section === "risks" && (
            <RisksView
              risks={workspace.risks}
              evidence={workspace.evidence}
              controls={workspace.criticalControls}
              revisions={workspace.documentRevisions}
              incidents={workspace.incidents}
              onSave={saveRisk}
              onRemove={removeRisk}
            />
          )}
          {section === "hirarc" && (
            <HirarcView
              assessments={workspace.hirarcAssessments}
              risks={workspace.risks}
              evidence={workspace.evidence}
              onSave={saveHirarc}
              onRemove={(id) =>
                onWorkspaceChange({
                  ...workspace,
                  hirarcAssessments: workspace.hirarcAssessments.filter((item) => item.id !== id),
                })
              }
            />
          )}
          {section === "controls" && (
            <ControlsView
              controls={workspace.criticalControls}
              risks={workspace.risks}
              evidence={workspace.evidence}
              onSave={saveControl}
              onRemove={(id) =>
                onWorkspaceChange({
                  ...workspace,
                  criticalControls: workspace.criticalControls.filter((item) => item.id !== id),
                  incidents: workspace.incidents.map((item) => ({
                    ...item,
                    linkedControlIds: item.linkedControlIds.filter((controlId) => controlId !== id),
                  })),
                })
              }
            />
          )}
          {section === "incidents" && (
            <IncidentsView
              incidents={workspace.incidents}
              risks={workspace.risks}
              controls={workspace.criticalControls}
              onSave={saveIncident}
              onRemove={(id) =>
                onWorkspaceChange({
                  ...workspace,
                  incidents: workspace.incidents.filter((item) => item.id !== id),
                })
              }
            />
          )}
          {section === "documents" && (
            <DocumentsView
              revisions={workspace.documentRevisions}
              comparisons={workspace.revisionComparisons}
              risks={workspace.risks}
              pins={workspace.safetyIssuePins}
              canUpload={Boolean(cloud)}
              onSave={saveRevision}
              onRemove={(id) =>
                onWorkspaceChange({
                  ...workspace,
                  documentRevisions: workspace.documentRevisions.filter((item) => item.id !== id),
                  safetyIssuePins: workspace.safetyIssuePins.filter(
                    (item) => item.documentRevisionId !== id,
                  ),
                  risks: workspace.risks.map((risk) => ({
                    ...risk,
                    revisionIds: risk.revisionIds.filter((revisionId) => revisionId !== id),
                  })),
                })
              }
              onUpload={uploadRevisionFile}
              onOpen={openRevisionFile}
              onSavePin={(pin) =>
                onWorkspaceChange({
                  ...workspace,
                  safetyIssuePins: upsertById(workspace.safetyIssuePins, pin),
                })
              }
              onRemovePin={(id) =>
                onWorkspaceChange({
                  ...workspace,
                  safetyIssuePins: workspace.safetyIssuePins.filter((item) => item.id !== id),
                })
              }
              onSaveComparison={(comparison) =>
                onWorkspaceChange({
                  ...workspace,
                  revisionComparisons: upsertById(
                    workspace.revisionComparisons,
                    comparison,
                  ),
                })
              }
              onImportPins={(importedPins) =>
                onWorkspaceChange({
                  ...workspace,
                  safetyIssuePins: [
                    ...workspace.safetyIssuePins,
                    ...importedPins.filter(
                      (pin) =>
                        !workspace.safetyIssuePins.some(
                          (existing) => existing.bcfGuid === pin.bcfGuid,
                        ),
                    ),
                  ],
                })
              }
            />
          )}
          {section === "deliverables" && (
            <DeliverablesView
              workspace={workspace}
              onSave={saveDeliverable}
              onRequestApproval={saveApproval}
            />
          )}
          {section === "changes" && (
            <RegulatoryChangesView
              project={workspace.project}
              changes={workspace.regulatoryChanges}
              dutyActions={workspace.dutyActions}
              risks={workspace.risks}
              onSave={saveRegulatoryChange}
              onRemove={(id) =>
                onWorkspaceChange({
                  ...workspace,
                  regulatoryChanges: workspace.regulatoryChanges.filter(
                    (item) => item.id !== id,
                  ),
                })
              }
            />
          )}
          {section === "field" && (
            <FieldVerificationView
              workspace={workspace}
              verifications={workspace.fieldVerifications}
              onSave={saveFieldVerification}
              onRemove={(id) =>
                onWorkspaceChange({
                  ...workspace,
                  fieldVerifications: workspace.fieldVerifications.filter(
                    (item) => item.id !== id,
                  ),
                })
              }
            />
          )}
          {section === "competence" && (
            <CompetenceView
              records={workspace.competenceRecords}
              assignments={workspace.trainingAssignments}
              onSaveRecord={saveCompetence}
              onRemoveRecord={(id) =>
                onWorkspaceChange({
                  ...workspace,
                  competenceRecords: workspace.competenceRecords.filter((item) => item.id !== id),
                })
              }
              onSaveAssignment={saveTraining}
            />
          )}
          {section === "approvals" && (
            <ApprovalsView
              approvals={workspace.approvals}
              risks={workspace.risks}
              hirarc={workspace.hirarcAssessments}
              controls={workspace.criticalControls}
              revisions={workspace.documentRevisions}
              onSave={saveApproval}
            />
          )}
          {section === "evidence" && (
            <EvidenceView
              evidence={workspace.evidence}
              risks={workspace.risks}
              onSave={saveEvidence}
              onRemove={removeEvidence}
            />
          )}
          {section === "reports" && <ReportsView workspace={workspace} />}
        </div>
      </main>
    </div>
  );
}

function CloudToolbar({ controls }: { controls: CloudControls }) {
  const stateLabel =
    controls.saveState === "saved"
      ? "Saved securely"
      : controls.saveState === "saving"
        ? "Saving…"
        : "Save needs attention";
  const StateIcon = controls.saveState === "error" ? CloudOff : Cloud;

  return (
    <div className="cloud-toolbar">
      <div className="cloud-organization">
        <Building2 size={18} />
        <span>
          <small>Organisation</small>
          <strong>{controls.organization.name}</strong>
        </span>
      </div>
      <label className="cloud-project-select">
        <span>Project</span>
        <select
          value={controls.activeProjectId}
          onChange={(event) => void controls.onProjectChange(event.target.value)}
        >
          {controls.projects.map((project) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      </label>
        <button className="cloud-action" type="button" onClick={controls.onNewProject}>
          <Plus size={17} /> New project
        </button>
        <button 
          className="cloud-action" 
          type="button" 
          style={{ color: "var(--red)" }}
          onClick={() => {
            if (confirm("Are you sure you want to delete this project? This cannot be undone.")) {
              void controls.onDeleteProject();
            }
          }}
          disabled={!controls.activeProjectId}
        >
          <Trash2 size={17} /> Delete project
        </button>
        <button
          className={`cloud-save cloud-save-${controls.saveState}`}
        type="button"
        onClick={() => void controls.onSave()}
      >
        <StateIcon size={17} />
        <span>
          <small>{controls.userEmail}</small>
          <strong>{stateLabel}</strong>
        </span>
      </button>
      <button
        className="cloud-signout"
        type="button"
        onClick={() => void controls.onSignOut()}
        aria-label="Sign out"
        title="Sign out"
      >
        <LogOut size={18} />
      </button>
    </div>
  );
}
